import React from 'react';
import { Alert, Button, CircularProgress, Stack, TextField } from '@mui/material';
import { CheckCircleOutline, MarkEmailReadOutlined } from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import AuthShell from '../components/AuthShell';
import { AuthAPI } from '../api/auth';
import { useAuth } from '../providers/AuthProvider';
import { ROUTES } from '../routes';

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = params.get('token');
  const emailParam = params.get('email') || '';
  const [email, setEmail] = React.useState(emailParam);
  const [loading, setLoading] = React.useState(Boolean(token));
  const [success, setSuccess] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const run = async () => {
      if (!token) return;
      try {
        const res = await AuthAPI.verifyEmail(token);
        const data = res?.data || {};
        if (data?.token) {
          auth.setSession(data);
        }
        setSuccess(data?.message || 'Email verified successfully.');
        setTimeout(() => navigate(ROUTES.WORKSPACE, { replace: true }), 1800);
      } catch (err) {
        setError(err?.response?.data?.message || 'Invalid or expired verification link.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token, navigate, auth]);

  const resend = async () => {
    setError('');
    setSuccess('');
    if (!email.trim()) {
      setError('Enter your email to resend the verification link.');
      return;
    }
    try {
      await AuthAPI.resendVerification(email.trim());
      setSuccess('Verification link sent. Please check your inbox.');
    } catch {
      setSuccess('Verification link sent. Please check your inbox.');
    }
  };

  return (
    <AuthShell
      title="Verify your email"
      subtitle="Complete verification before signing in."
      sideTitle="Verification keeps the tenant secure"
      sideBody="We only allow access after email verification so each enterprise starts with a trusted identity."
    >
      <Stack spacing={2}>
        {loading ? <CircularProgress /> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        {!token ? (
          <>
            <TextField
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
            <Button variant="contained" onClick={resend} startIcon={<MarkEmailReadOutlined />}>
              Resend verification link
            </Button>
          </>
        ) : null}

        <Button component={RouterLink} to={ROUTES.LOGIN} variant="outlined" startIcon={<CheckCircleOutline />}>
          Back to sign in
        </Button>
      </Stack>
    </AuthShell>
  );
}
